import React, { useContext, useEffect, useCallback, useState, useRef } from 'react';
import {
  Grid,
  Button,
  Paper,
  Dialog,
  Snackbar,
  TextField,
  Typography,
  IconButton,
  DialogTitle,
  DialogContent,
  CircularProgress
} from '@material-ui/core';
import Rating from '@material-ui/lab/Rating';
import { withStyles } from '@material-ui/core/styles';
import { useForm, Controller } from 'react-hook-form';
import CloseIcon from '@material-ui/icons/Close';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import { yupResolver } from '@hookform/resolvers';
import { I18nContext } from 'translations';
import { format } from 'date-fns';
import { uploadFile } from 'utils/aws';
import { useStateValue } from 'context/store';
import { showPqr, answerPqr, ratePqr } from 'context/claims/actions';
import { message as closeMessage } from 'context/commons/actions';
import UploadNormalFile from 'components/CustomInputs/UploadNormalFile';
import SendIcon from '@material-ui/icons/Send';
import styles from '../../Tables/styleTableFilters';
import schema from './schemaClaimsTracking';

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];

const getFileExtension = (url) => url?.split('?')[0].split('.').pop().toLowerCase();

const isImage = (url) => IMAGE_EXTENSIONS.includes(getFileExtension(url));

function DocumentPreview({ url }) {
  if (!url) return null;
  if (isImage(url)) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginTop: 6 }}>
        <img
          src={url}
          alt="adjunto"
          style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 8, objectFit: 'cover', display: 'block', cursor: 'pointer' }}
        />
      </a>
    );
  }
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, textDecoration: 'none', color: 'inherit' }}>
      <div style={{ background: '#e3e3e3', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <InsertDriveFileIcon style={{ color: '#7825bd', fontSize: 28 }} />
        <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#555' }}>
          {decodeURIComponent(url.split('/').pop().split('?')[0])}
        </span>
      </div>
    </a>
  );
}

const defaultValues = {
  mssg: '',
  document: null
}

function ModalClaimsTracking ({
  open,
  role,
  openClaim,
  id
}) {
  const { translate } = useContext(I18nContext);
  const [loading, setLoading] = useState(false);
  const [loadingPqr, setLoadingPqr] = useState(false);
  const [messageImage, setMessageImage] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [ratingLoading, setRatingLoading] = useState(false);
  const [selectedRating, setSelectedRating] = useState(null);
  const divRef = useRef(null);
  const [
    {
      commons: { message },
      claims: { pqr },
      checkin: { checkin }
    },
    dispatch,
  ] = useStateValue();

  const currentUserName = checkin?.profile?.name;

  const { handleSubmit, control, errors, reset, register, watch } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema)
  });

  const loadedPqr = () => {
    setLoadingPqr(false)
    const scrollHeight = divRef.current.scrollHeight;
    const height = divRef.current.clientHeight;
    const maxScrollTop = scrollHeight - height;
    divRef.current.scrollTop = maxScrollTop > 0 ? maxScrollTop : 0;
  }

  const getPqr = useCallback(
    async () => {
      setLoadingPqr(true);
      const callback = await showPqr(dispatch, id);
      callback.status ? loadedPqr() : setLoadingPqr(false)
    },
    [dispatch, id]
  );

  useEffect(() => {
    getPqr()
  }, [getPqr])

  const hasRatedThisClosure = pqr?.messages?.some(
    m => m.rating != null && (!pqr.reopened_at || new Date(m.created_at) > new Date(pqr.reopened_at))
  );

  const mssgSend = async () => {
    setLoading(false);
    await showPqr(dispatch, id)
    reset(defaultValues);
    // Forzar la limpieza del input de archivo
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
    setResetKey(prev => prev + 1);
    loadedPqr();
  }

  const uploadFileAws = async (file) => {
    const date = Date.parse(new Date());

    // Define los límites según el tipo de archivo
    const videoMaxSize = 50 * 1024 * 1024; // 50 MB
    const otherMaxSize = 5 * 1024 * 1024; // 5 MB

    // Verificar si es un video
    const isVideo = file?.type.startsWith('video');

    // Comparar con el tamaño máximo correspondiente
    if (file?.size <= (isVideo ? videoMaxSize : otherMaxSize)) {
      return await uploadFile(file, `pqr/${date}`);
    } else {
      alert(
        `The file size exceeds the limit. Maximum size is ${
          isVideo ? '50 MB for videos' : '5 MB for other files'
        }.`
      );
      return false; // Indica que la subida no se realizó
    }
  };

  const submit = async values => {
    setLoading(true)
    const responseAttach = values?.document && values.document[0] ? await uploadFileAws(values.document[0]) : null
    const data = {
      ...values,
      document: responseAttach?.Location
    }
    if (responseAttach === true) {
      setMessageImage('claims.errorSizeFile')
      setLoading(false)
    } else {
      setMessageImage(null)
      const callback = await answerPqr(dispatch, id, { pqr: { ...data } })
      callback?.status ? mssgSend() : setLoading(false);
    }
  }

  const handleRating = (_, newValue) => {
    if (hasRatedThisClosure) return;
    setSelectedRating(newValue);
  };

  const submitRating = async () => {
    if (!selectedRating) return;
    setRatingLoading(true);
    await ratePqr(dispatch, id, selectedRating);
    setRatingLoading(false);
  };

  return (
    <Dialog
      open={open}
      aria-describedby="alert-dialog-description"
      maxWidth="md"
      fullWidth
      style={{ padding: '10px 40px 20px' }}
    >
      <DialogTitle disableTypography style={{ paddingBottom: 0 }}>
        <Typography variant="h6">
          <b>{translate('claims.claim')}</b>
          <b>{translate('claims.ticketId')}: {pqr?.ticket_number || pqr?.id}</b>
        </Typography>
        <IconButton aria-label="close"  onClick={openClaim} style={{ position: 'absolute', right: 0, top: 0 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {pqr && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
            {[
              { label: translate('claims.table.date'), value: pqr.created_at ? format(new Date(pqr.created_at), 'dd/MM/yyyy, hh:mm a') : '-' },
              { label: translate('claims.form.typeOfPqr'), value: pqr.subject || '-' },
              { label: translate('claims.table.subject'), value: pqr.type_of_request || '-' },
            ].map(({ label, value }) => (
              <div key={label} style={{ background: '#f5f5f5', borderRadius: 8, padding: '5px 12px', minWidth: 120 }}>
                <Typography variant="caption" style={{ color: '#888', display: 'block', lineHeight: 1.2 }}>{label}</Typography>
                <Typography variant="body1" style={{ fontWeight: 400, fontSize: '0.85rem' }}>{value}</Typography>
              </div>
            ))}
          </div>
        )}
        {loadingPqr ? <div className="text-center"><CircularProgress color="primary" /></div> :
        <Grid container>
          <Grid item xs={12}>
            <Paper
              variant="outlined"
              ref={divRef}
              style={{
                padding: '10px 10px 25px', borderRadius: 10, marginBottom: 8, maxHeight: 'calc(100vh - 420px)', minHeight: '150px', overflowY: 'auto'
              }}
            >
              {pqr?.messages?.length > 0
                ? [...pqr.messages].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)).map((mssg, index) => {
                  const isOwn = mssg?.sender_type === 'company' ||
                    mssg?.sender_type === 'passenger' ||
                    (mssg?.sender_type === null && currentUserName && (
                      mssg?.passenger_name === currentUserName ||
                      mssg?.passenger_name === pqr.passenger_name
                    ));
                  return (
                    <div key={`mssg-${index}`} style={{ display: 'flex', flexDirection: 'column', alignItems: isOwn ? 'flex-end' : 'flex-start', marginBottom: 16 }}>
                      <Typography variant="caption" style={{ color: isOwn ? '#7825bd' : '#1565c0', fontWeight: 700, marginBottom: 2 }}>
                        {mssg?.passenger_name}
                      </Typography>
                      {mssg?.created_at && (
                        <Typography variant="caption" style={{ color: '#bdbdbd', fontSize: '0.65rem', fontWeight: 600, marginBottom: 4 }}>
                          {format(new Date(mssg.created_at), 'dd/MM/yyyy, hh:mm a')}
                        </Typography>
                      )}
                      <div style={{
                        background: isOwn ? '#f3e8ff' : '#f7f7f7',
                        border: isOwn ? '1px solid #d8b4fe' : '1px solid #eeeeee',
                        borderRadius: isOwn ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                        padding: '10px 14px',
                        maxWidth: '72%',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                      }}>
                        {mssg.rating != null ? (
                          <Rating value={mssg.rating} readOnly size="medium" />
                        ) : (
                          <>
                            {mssg?.mssg &&
                              (mssg.mssg.includes("<") && mssg.mssg.includes(">") ? (
                                <span style={{ color: isOwn ? '#6b21a8' : '#212121', fontSize: '0.875rem' }} dangerouslySetInnerHTML={{ __html: mssg.mssg }} />
                              ) : (
                                <Typography variant="body2" style={{ color: isOwn ? '#6b21a8' : '#212121', lineHeight: 1.5 }}>{mssg.mssg}</Typography>
                              ))}
                            {mssg?.document && <DocumentPreview url={mssg.document} />}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })
                : translate('claims.noMessages')
              }
              {pqr?.status_cd === 4 && !hasRatedThisClosure && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 16, paddingTop: 12, borderTop: '1px solid #eee' }}>
                  <Typography variant="body1" style={{ color: '#555', marginBottom: 10 }}>
                    <b>{translate('claims.rating.rate')}</b>
                  </Typography>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Rating
                      value={selectedRating}
                      onChange={handleRating}
                      readOnly={ratingLoading}
                      size="large"
                    />
                  </div>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    style={{ borderRadius: '17px', marginTop: 10 }}
                    disabled={!selectedRating || ratingLoading}
                    onClick={submitRating}
                    endIcon={ratingLoading && <CircularProgress color="inherit" size="1rem" />}
                  >
                    {translate('send')}
                  </Button>
                </div>
              )}
            </Paper>
            <Paper variant="outlined" style={{ padding: '10px 10px 15px', borderRadius: 10, marginBottom: 15 }}>
              <Typography style={{ marginBottom: 8, textTransform: 'uppercase' }}>{translate('claims.answerTo')} {translate('claims.claimsCoordinator')}</Typography>

              <form onSubmit={handleSubmit(submit)} noValidate>
                <Grid container>
                  <Grid item xs={12} style={{ marginBottom: 10 }}>
                    <Controller
                      name="mssg"
                      control={control}
                      variant="outlined"
                      render={({ onChange, value }) => (
                        <>
                          <TextField
                            size="small"
                            variant="outlined"
                            label={translate('claims.form.writeAResponse')}
                            fullWidth
                            value={value}
                            onChange={onChange}
                            multiline
                            rows={3}
                            error={!!errors.mssg}
                            helperText={errors.mssg && translate(errors.mssg.message)}
                          />

                          {pqr?.status_cd === 4 && (
                            <div style={{ display: 'flex', alignItems: 'flex-start', marginTop: 8 }}>
                              <div style={{ marginRight: 8, flexShrink: 0 }}>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none"
                                  xmlns="http://www.w3.org/2000/svg">
                                  <path d="M11 14H10V10H9M10 6H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z"
                                    stroke="#226ADE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </div>
                              <div>
                                <Typography
                                  variant="body2"
                                  style={{
                                    color: '#226ADE',
                                    fontWeight: 600,
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {translate('claims.reopenMessage.title')}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  style={{
                                    color: '#226ADE',
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {translate('claims.reopenMessage.subtitle')}
                                </Typography>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    />
                  </Grid>
                  <Grid item md={5} xs={12}>
                    <UploadNormalFile
                      key={`upload-${id}-${resetKey}`}
                      text={translate('claims.form.attachFile')}
                      register={register}
                      fileName="document"
                      nameFile={watch('document')}
                      accept="image/*,.pdf,video/*"
                      helpText={translate('claims.formatFile')}
                    />
                  </Grid>
                  <Grid item md={7} xs={12} className="text-right" >
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="small"
                      style={{ borderRadius: '17px' }}
                      disabled={loading}
                      endIcon={loading ? <CircularProgress color="inherit" size="1rem" /> : <SendIcon color="inherit" size="1rem" />}
                    >
                      {translate('send')}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Grid>
        </Grid>
        }
      </DialogContent>
      <Snackbar
        style={{ zIndex: 99999 }}
        open={!!message}
        autoHideDuration={6000}
        onClose={() => closeMessage(dispatch, null)}
        message={translate(message)}
      />
      <Snackbar
        style={{ zIndex: 99999 }}
        open={!!messageImage}
        autoHideDuration={6000}
        onClose={() => setMessageImage(null)}
        message={translate(messageImage)}
      />
    </Dialog>
  )
}

export default withStyles(styles)(ModalClaimsTracking);
